import { useMemo } from 'react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { renderTemplateWithDiagnostics, humanizeToken, type ContactLike } from '@/lib/email/oneoff-merge';

export interface PopulateResult { subject: string; body: string; }

export interface TemplatePopulateDialogProps {
  open: boolean;
  template: { subject: string; body: string } | null;
  contact: ContactLike | null;
  onPopulate: (r: PopulateResult) => void;
  onInsertRaw: () => void;
  onCancel: () => void;
}

export default function TemplatePopulateDialog({
  open, template, contact, onPopulate, onInsertRaw, onCancel,
}: TemplatePopulateDialogProps) {
  const computed = useMemo(() => {
    if (!template || !contact) return null;
    const subj = renderTemplateWithDiagnostics(template.subject, contact);
    const body = renderTemplateWithDiagnostics(template.body, contact);
    const missing = Array.from(new Set([...subj.missing, ...body.missing]));
    const unknown = Array.from(new Set([...subj.unknown, ...body.unknown]));
    return { rendered: { subject: subj.rendered, body: body.rendered }, missing, unknown };
  }, [template, contact]);

  if (!template || !contact || !computed) return null;
  const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim() || 'this contact';

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Populate from {name}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <div className="rounded-md border bg-muted/50 p-3 text-sm">
                <div className="font-medium text-foreground">{name}</div>
                {contact.primary_email && <div className="text-xs text-muted-foreground">{contact.primary_email}</div>}
                {contact.primary_phone && <div className="text-xs text-muted-foreground">{contact.primary_phone}</div>}
              </div>

              {computed.missing.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm">
                  <div className="mb-1 font-medium text-foreground">
                    This contact has no data for:
                  </div>
                  <ul className="ml-4 list-disc">
                    {computed.missing.map((t) => (
                      <li key={t}>{humanizeToken(`{{${t}}}`)} — <code className="text-xs">{`{{${t}}}`}</code> will stay in the email</li>
                    ))}
                  </ul>
                </div>
              )}

              {computed.unknown.length > 0 && (
                <div className="rounded-md border bg-muted/50 p-3 text-sm">
                  <div className="mb-1 font-medium text-foreground">Not recognized (left as-is):</div>
                  <ul className="ml-4 list-disc font-mono text-xs">
                    {computed.unknown.map((t) => <li key={t}>{`{{${t}}}`}</li>)}
                  </ul>
                </div>
              )}

              <p className="text-sm text-muted-foreground">Always double-check the email before sending.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button variant="outline" onClick={onInsertRaw}>Apply without populating</Button>
          <Button onClick={() => onPopulate(computed.rendered)}>Apply &amp; populate</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
